import 'mapbox-gl/dist/mapbox-gl.css';

import {
  assert,
  assertSome,
  isNone,
  isSome,
  Maybe,
  noop,
} from '@crocodille/common';
import cn from 'classnames';
import { Map as Mapbox } from 'mapbox-gl';
import React, { useEffect, useMemo, useRef } from 'react';
import { v2_useBranchDeliveryMap } from '@crocodille/common';

import styles from './branch-map.module.scss';
import branchPolygons from './branch-polygons.json';

import type { Feature, Point, Polygon } from 'geojson';

const DEFAULT_CENTER = [14.3968, 50.1001] as [number, number];

const AREA_FEATURES = branchPolygons.map((bp) => {
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Polygon' as const,
      coordinates: [bp.polygon],
    },
    properties: {
      codes: bp.codes,
    },
  };
});

function applyBranchHighlight(map: Mapbox, highlightBranch?: Maybe<string>) {
  const filter = isSome(highlightBranch)
    ? ['in', highlightBranch, ['get', 'codes']]
    : undefined;
  map.setFilter('branches_fill', filter);

  if (filter) {
    const features = map.querySourceFeatures('branches', { filter });
    const center = features.find((f) => f.geometry.type === 'Point') as Maybe<
      Feature<Point>
    >;

    if (center) {
      map.flyTo({ center: center.geometry.coordinates as [number, number] });
    }
  }
}

export interface MapProps {
  readonly className?: Maybe<string>;
  readonly highlightBranch?: Maybe<string>;
  readonly branchPoints?: Maybe<
    readonly {
      readonly code: string;
      readonly name: string;
      readonly location: readonly [number, number];
    }[]
  >;
  onBranchClicked?(branchCode: string): void;
  readonly userLocation?: Maybe<[number, number]>;
}

const BranchMap: React.FC<React.PropsWithChildren<MapProps>> = ({
  className,
  highlightBranch,
  branchPoints,
  onBranchClicked = noop,
  userLocation,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Maybe<Mapbox>>(null);
  const { data: branchMapData } = v2_useBranchDeliveryMap();

  const highlightBranchRef = useRef(highlightBranch);
  highlightBranchRef.current = highlightBranch;

  const pointFeatures = useMemo(
    () =>
      (branchMapData?.branchLocations ?? branchPoints)?.map?.((bp) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: bp.location as unknown as number[],
        },
        properties: {
          name: bp.name,
          codes: [bp.code],
        },
      })),
    [branchPoints, branchMapData],
  );

  const pointFeaturesRef = useRef(pointFeatures);
  pointFeaturesRef.current = pointFeatures;

  const userFeature = useMemo(() => {
    if (isNone(userLocation)) {
      return [];
    }

    return [
      {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [userLocation?.[0], userLocation?.[1]],
        },
        properties: {},
      },
    ];
  }, [userLocation]);

  const userFeatureRef = useRef(userFeature);
  userFeatureRef.current = userFeature;

  const areaFeatures = useMemo(() => {
    return (
      branchMapData?.deliveryAreas?.map?.((bp) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: [bp.polygon],
        },
        properties: {
          codes: bp.codes,
        },
      })) ?? AREA_FEATURES
    );
  }, [branchMapData]);

  useEffect(() => {
    const map = new Mapbox({
      accessToken: assertSome(process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN),
      container: assertSome(containerRef.current),
      style: 'mapbox://styles/mapbox/light-v10',
      center: userLocation ?? DEFAULT_CENTER,
      zoom: 10,
    });

    map.on('load', () => {
      map.addSource('branch_areas', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [...areaFeatures],
        },
      });

      map.addSource('branches', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: pointFeaturesRef.current ?? [],
        },
      });

      map.addSource('user', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: userFeatureRef.current ?? [],
        },
      });

      const layers = map.getStyle().layers;
      // Find the index of the first symbol layer in the map style.
      let firstSymbolId;
      for (const layer of layers) {
        if (layer.type === 'line') {
          firstSymbolId = layer.id;
          break;
        }
      }

      map.addLayer(
        {
          id: `branches_fill`,
          type: 'fill',
          source: 'branch_areas',
          layout: {},
          paint: {
            'fill-color': '#f1b600',
            'fill-opacity': 0.5,
          },
        },
        firstSymbolId,
      );

      map.addLayer({
        id: `branches_label`,
        type: 'symbol',
        source: 'branches',
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.05,
          'text-offset': [0, 1.5],
        },
        paint: {
          'text-color': '#583017',
          'text-opacity': 0.8,
        },
      });

      map.addLayer({
        id: `branches_point`,
        type: 'circle',
        source: 'branches',
        filter: ['!=', ['get', 'name'], null],
        layout: {},
        paint: {
          'circle-radius': 3,
          'circle-color': '#583017',
          'circle-opacity': 0.8,
        },
      });

      map.addLayer({
        id: `user_point`,
        type: 'circle',
        source: 'user',
        layout: {},
        paint: {
          'circle-radius': 4,
          'circle-color': '#f70000',
          'circle-opacity': 0.7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#583017',
          'circle-stroke-opacity': 0.8,
        },
      });

      map.on('click', ['branches_label', 'branches_point'], (e) => {
        const feature = e.features?.find?.((f) =>
          isSome(f.properties?.codes),
        ) as Maybe<Feature<Polygon>>;

        if (isNone(feature) || !feature.properties?.codes) {
          return;
        }

        const codes = JSON.parse(feature.properties.codes);

        if (Array.isArray(codes) && isSome(codes[0])) {
          onBranchClicked(codes[0]);
        }
      });

      // Change the cursor to a pointer when the it enters a feature in the 'circle' layer.
      map.on('mouseenter', ['branches_label', 'branches_point'], () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      // Change it back to a pointer when it leaves.
      map.on('mouseleave', ['branches_label', 'branches_point'], () => {
        map.getCanvas().style.cursor = '';
      });

      applyBranchHighlight(map, highlightBranchRef.current);

      mapRef.current = map;
    });

    return () => {
      mapRef.current = null;
      map.remove();
    };
  }, []);

  useEffect(() => {
    if (isNone(mapRef.current)) {
      return;
    }

    if (isSome(pointFeatures)) {
      const source = mapRef.current.getSource('branches');

      assert(source.type === 'geojson');

      source.setData({
        type: 'FeatureCollection',
        features: pointFeatures,
      });
    }
  }, [pointFeatures]);

  useEffect(() => {
    if (isSome(mapRef.current)) {
      applyBranchHighlight(mapRef.current, highlightBranch);
    }
  }, [highlightBranch]);

  useEffect(() => {
    if (isNone(mapRef.current)) {
      return;
    }

    const source = mapRef.current.getSource('user');

    assert(source.type === 'geojson');

    source.setData({
      type: 'FeatureCollection',
      features: userFeature ?? [],
    });
  }, [userFeature]);

  useEffect(() => {
    if (isNone(mapRef.current)) {
      return;
    }

    const source = mapRef.current.getSource('branch_areas');

    assert(source.type === 'geojson');

    source.setData({
      type: 'FeatureCollection',
      features: areaFeatures ?? [],
    });
  }, [areaFeatures]);

  useEffect(() => {
    if (isNone(mapRef.current)) {
      return;
    }

    if (userLocation) {
      mapRef.current.flyTo({ center: userLocation });
    }
  }, [userLocation]);

  return <div ref={containerRef} className={cn(styles.container, className)} />;
};

export default BranchMap;
